import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../lib/api';
import type { ConsentInfo } from './types';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface ConsentInfoError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): ConsentInfoError {
  const e = new Error(stage) as ConsentInfoError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

export const consentInfoQueryKey = (id: string) => ['consent', 'info', id] as const;

/**
 * Fetches `GET /auth/consent-info?authorize_request_id=…`. Returns the
 * client and scope metadata needed to render the consent card. The endpoint
 * requires an authenticated session; a 401 surfaces as `status: 401` so the
 * route can bounce to `/login`.
 */
export function useConsentInfo(authorizeRequestId: string | undefined) {
  const api = useApi();
  return useQuery<ConsentInfo, ConsentInfoError>({
    queryKey: consentInfoQueryKey(authorizeRequestId ?? ''),
    enabled: Boolean(authorizeRequestId),
    queryFn: async () => {
      const { data, error, response } = (await api.GET('/auth/consent-info' as never, {
        params: { query: { authorize_request_id: authorizeRequestId } },
      } as never)) as { data?: ConsentInfo; error?: unknown; response?: Response };
      if (error || !data) throw toError('consent_info_failed', response, error);
      return data;
    },
    retry: false,
    staleTime: 0,
  });
}
